import React from "react";
import BaseModel from "../models/BaseModel";
import type Floor from "../models/Floor";
import FloorDialog from "../modelDialogs/FloorDialog";
import RoomDialog from "../modelDialogs/RoomDialog";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";
import BaseModelMap from "../models/BaseModelMap";
import FloorPropertyView from "../modelPropertyViews/FloorPropertyView";

import Logger from "../Logger";
import { itx, noItx } from "../translationItx/itx";
import Page from "../models/Page";
import { PageTypes } from "../models/PageTypes";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";
import { isUiModeDeveloper } from "../uiMode";
import { C1_Floor } from "../../ConceptOne/src/model/C1_Floor";
import { ApplicationMode } from "../models/SettingsEnums";

const logger = new Logger();

export default class FloorTreeViewModel extends TreeViewModel {
  get typedObj(): Floor {
    return this.obj as Floor;
  }

  get isFolder(): boolean {
    return true;
  }

  getTagNeeded(): boolean {
    return true;
  }

  get childCollection(): Array<BaseModel> | undefined {
    let res = this.typedObj.rooms.sortBy("tagOrName");

    res = res.filter((room) => {
      const emptyFloorGeneral = room.tag == "" && room.name == "" && room.consumers.size == 0 && room.buttons.size == 0;
      return !emptyFloorGeneral;
    });

    return res;
  }

  get iconUrl(): string | undefined {
    if (this.typedObj) {
      return "las la-layer-group"; // no itx
    }

    return undefined;
  }

  getPropertyControl(setPinned: (pinned: boolean) => void): any {
    if (this.typedObj.tag.startsWith("@I")) {
      return undefined;
    }

    return <FloorPropertyView obj={this.obj} setPinned={setPinned} />;
  }

  handleOpen(): boolean {
    // if (this.typedObj.page == undefined) {
    //   this.typedObj.page = new Page({}, this.typedObj);
    //   //
    //   // Set Default PageSize for Floors to A3
    //   //
    //   this.typedObj.page.pagesize = {
    //     width: 42,
    //     height: 29.7,
    //   };
    //   this.typedObj.page.parent = this.typedObj;
    //   this.typedObj.page.documentType.type = PageTypes.floorplan;
    //   this.typedObj.page.documentType.name = state.translate("pageDocumentType.floorplan", "Etagenplan");
    // }

    if (state.projectMetadata.isC1Project && !this.typedObj.c1) {
      const levels = state.floors.asArray().flatMap(floor =>
        floor.c1 && Number.isFinite(floor.c1.level) ? [floor.c1.level] : [],
      );
      this.typedObj.c1 = new C1_Floor({
        level: levels.length ? Math.max(...levels) + 1 : 0,
        ownCabinet: false,
      }, this.typedObj);
    }

    const isC1Floor = this.typedObj.c1 !== undefined;
    const pageId = isC1Floor ? this.typedObj.id : (this.typedObj as Floor).getOrCreatePage().id;

    if (state.activePanes.isOpen(pageId)) {
      state.activePanes.activatePane(pageId);
    } else {
      state.activePanes.loadPage(pageId);
    }

    return true;
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // inline menu buttons
  //super.inlineButton_Properties(),
  ////////////////////////////////////////////////////////////////////////////////////////

  getButtons(): JSX.Element[] | undefined {
    const res: JSX.Element[] = [];

    if (this.typedObj.tag.startsWith("@I")) {
      res.push(
        <TreeMenuIcon
          onClick={async () => {
            //
            // hier mit import für lazy loading, getrenntes bundle für ets import
            //
            const module = await import("../extensions/imports/ets/postImport/moveObjects");
            const CreateObjectsFromImport = module.default;
            state.dialogManager.openDialog(<CreateObjectsFromImport rootObject={this.typedObj} />, false, false);
          }}
          iconName="star" // no itx
          // tooltipKey={tooltipKey}
          //          tooltipText={itx("deviceTreeViewModel.syncETS", "Sync ETS")}
          objName="CreateObjects" // no itx
          key="CreateObjects"
        />,
      );
    }

    return [
      ...res,
      super.inlineButton_Open("inlineButton.editFloor"),
      ...(state.settings.applicationMode === ApplicationMode.ConceptOne && state.projectMetadata.isC1Project ? [] : [super.inlineButton_Add("ContextMenuLables.addRoom")]),
    ]; // no itx
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // context menu
  //
  ////////////////////////////////////////////////////////////////////////////////////////

  handleAdd() {
    state.dialogManager.openDialog(<RoomDialog parent={this.typedObj} />);
  }

  handleProperties() {
    state.dialogManager.openDialog(<FloorDialog parent={this.obj.parent as BaseModelMap<Floor>} obj={this.typedObj} />);
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  canBePastedInteractive(copiedObjClassName: string): boolean {
    if (copiedObjClassName == "Room" || copiedObjClassName == "Floor" || copiedObjClassName == "Actor") {
      return true;
    }

    return false;
  }
  ////////////////////////////////////////////////////////////

  async getDisabledContextMenuItems(): Promise<Array<string>> {
    const copiedObjClassName = state.clipboard.getClassName();

    if (
      (copiedObjClassName && copiedObjClassName == "Room") ||
      (copiedObjClassName && copiedObjClassName == "Floor") ||
      (copiedObjClassName && copiedObjClassName == "Actor")
    ) {
      return new Array<string>();
    }

    return ["paste"]; // no itx
  }

  allowDrag(): boolean {
    return true;
  }

  allowDrop(event: any): boolean {
    return true;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    try {
      const floor = this.typedObj;
      const generalRoom = floor.getRoomGeneral();
      const floorPage = floor.getOrCreatePage();

      const handleFromRoom = ["DeviceTemplate"]; // no itx
      const dontAllowBecauseOfPlacing = ["Button", "Consumer"]; // no itx

      if (handleFromRoom.includes(obj.className) && generalRoom) {
        return generalRoom.treeViewModel.handleDrop2(mode, obj);
      }

      if (dontAllowBecauseOfPlacing.includes(obj.className) && generalRoom) {
        return generalRoom.treeViewModel.handleDrop2(mode, obj);
        const errorMessage = itx(
          "floorTreeViewModel.relocationFromT1ToT2NotAllowed",
          "Das verschieben von T1 in die Etage T2 ist nicht erlaubt!",
          { T1: obj.tag + " " + obj.name, T2: this.typedObj.tag + " " + this.typedObj.name },
        );
        return new DropResult(false, errorMessage);
      }

      switch (obj.className) {
        case noItx("Page"):
          if (floorPage) {
            const pageDraged = obj as Page;
            if (pageDraged.documentType.type == PageTypes.pdf) {
              // set here the backgroundImgUrl
              const dpmm = 30;
              const backgrounimageBefore = floorPage.backgroundImgUrl;
              const imgUrl = await pageDraged.pdfFile.convertPdfToDataUrl({
                pageNumber: 1,
                targetWidth: floorPage.pagesize.width * dpmm,
                targetHeight: floorPage.pagesize.height * dpmm,
                rotation: floorPage.pdfRotation || 0,
              });

              const result = await floorPage.resizeImageAfterLoad(imgUrl);
              floorPage.backgroundImgUrl = result as string;

              floorPage.raiseOnChange();
              return new DropResult(true);
            }
          }
          break;
      }
    } catch (ex) {
      logger.error(ex);
    }
    return super.handleDrop2(mode, obj);
  }

  get contextMenuItems(): any {
    let subLabelPaste = undefined;

    try {
      subLabelPaste = state.contextMenuManager.clipboardData?.items?.[0]?.name;
    } catch (ex) {
      logger.error(ex);
    }

    return {
      title: this.obj?.name,
      items: [
        this.baseContextMenuItems_Copy(),
        // this.baseContextMenuItems_Cut(),
        this.baseContextMenuItems_Paste(subLabelPaste),
        this.baseContextMenuItems_Delete(),
        this.baseContextMenuItems_Add("ContextMenuLables.addRoom"), // no itx
        // this.baseContextMenuItems_Properties(),
      ],
    };
  }
}
